/**
 * Preload Scene
 * 
 * Handles asset loading with real progress bar.
 * Features:
 * - Real progress tracking (no fake loading)
 * - Graceful degradation for failed assets
 */

import Phaser from 'phaser';
import { SCENES, GAME_WIDTH, GAME_HEIGHT } from '../config/Constants';
import { PlatformManager } from '../sdk/PlatformManager';

export class PreloadScene extends Phaser.Scene {
  private loadingBar!: Phaser.GameObjects.Graphics;
  private loadingText!: Phaser.GameObjects.Text;
  private progressText!: Phaser.GameObjects.Text;
  private loadStartTime = 0;
  private failedAssets: string[] = [];
  private barX = 0;
  private barY = 0;
  private barWidth = 300;
  private barHeight = 20;
  
  constructor() {
    super({ key: SCENES.PRELOAD });
  }
  
  /**
   * Preload - Load all game assets
   */
  preload(): void {
    this.loadStartTime = Date.now();
    console.log('[PreloadScene] Starting asset load...');
    
    // Create loading UI
    this.createLoadingUI();
    
    // Setup load event handlers
    this.setupLoadHandlers();
    
    // Generate placeholder assets (since we don't have real art)
    this.generatePlaceholderAssets();
    
    // Load audio (generated procedurally)
    this.loadAudioAssets();
  }
  
  /**
   * Create loading UI elements
   */
  private createLoadingUI(): void {
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;
    
    // Background
    this.add.rectangle(centerX, centerY, GAME_WIDTH, GAME_HEIGHT, 0x0a0a1a);
    
    // Title
    this.add.text(centerX, centerY - 100, '🪐 Sticky Planet', {
      fontSize: '48px',
      fontFamily: '"Segoe UI", system-ui, sans-serif',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    // Loading bar dimensions
    this.barX = centerX - this.barWidth / 2;
    this.barY = centerY + 50;
    
    this.add.rectangle(centerX, this.barY + this.barHeight / 2, this.barWidth + 4, this.barHeight + 4, 0x333355)
      .setOrigin(0.5);
    
    // Loading bar (filled dynamically)
    this.loadingBar = this.add.graphics();
    this.loadingBar.fillStyle(0x00d4ff, 1);
    
    // Loading text
    this.loadingText = this.add.text(centerX, this.barY + 50, 'Loading assets...', {
      fontSize: '20px',
      fontFamily: '"Segoe UI", system-ui, sans-serif',
      color: '#888899'
    }).setOrigin(0.5);
    
    // Progress percentage
    this.progressText = this.add.text(centerX, this.barY + this.barHeight / 2, '0%', {
      fontSize: '14px',
      fontFamily: '"Segoe UI", system-ui, sans-serif',
      color: '#ffffff'
    }).setOrigin(0.5);
  }
  
  /**
   * Setup load event handlers
   */
  private setupLoadHandlers(): void {
    // Progress handler - update bar
    this.load.on('progress', (value: number) => {
      // Clear and redraw loading bar
      this.loadingBar.clear();
      this.loadingBar.fillStyle(0x00d4ff, 1);
      this.loadingBar.fillRect(this.barX, this.barY, this.barWidth * value, this.barHeight);
      
      // Update percentage text
      this.progressText.setText(`${Math.round(value * 100)}%`);
      
      // Update HTML loading bar too
      const htmlBar = document.getElementById('loading-bar');
      if (htmlBar) {
        htmlBar.style.width = `${value * 100}%`;
      }
      
      const htmlText = document.getElementById('loading-text');
      if (htmlText) {
        htmlText.textContent = `Loading... ${Math.round(value * 100)}%`;
      }
    });
    
    // File load handler
    this.load.on('fileprogress', (file: Phaser.Loader.File) => {
      this.loadingText.setText(`Loading: ${file.key}`);
    });
    
    // Load error handler
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[PreloadScene] Failed to load: ${file.key}`);
      this.failedAssets.push(file.key);
    });
    
    // Complete handler
    this.load.on('complete', () => {
      const loadTime = Date.now() - this.loadStartTime;
      console.log(`[PreloadScene] Load complete in ${loadTime}ms`);
      
      if (this.failedAssets.length > 0) {
        console.warn('[PreloadScene] Failed assets:', this.failedAssets);
      }
    });
  }
  
  /**
   * Generate placeholder assets programmatically
   */
  private generatePlaceholderAssets(): void {
    console.log('[PreloadScene] Using procedural assets (no external files needed)');
  }
  
  /**
   * Load audio assets
   */
  private loadAudioAssets(): void {
    console.log('[PreloadScene] Audio will be generated procedurally');
  }
  
  /**
   * Create - Called when all assets are loaded
   */
  create(): void {
    const loadTime = Date.now() - this.loadStartTime;
    console.log(`[PreloadScene] Total load time: ${loadTime}ms`);
    
    // Notify platform SDK that loading is complete
    PlatformManager.getInstance().gameLoadingFinished();
    
    // Hide HTML loading screen with fade
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
      setTimeout(() => {
        loadingScreen.style.display = 'none';
      }, 300);
    }
    
    // Store load time for performance tracking
    this.registry.set('initialLoadTime', loadTime);
    
    // Create audio manager
    this.createAudioSystem();
    
    // Small delay for visual polish, then start menu
    this.time.delayedCall(500, () => {
      this.scene.start(SCENES.MENU);
    });
  }
  
  /**
   * Create audio system with procedural sounds
   */
  private createAudioSystem(): void {
    try {
      const AudioContextClass = window.AudioContext || 
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (AudioContextClass) {
        const audioContext = new AudioContextClass();
        this.registry.set('audioContext', audioContext);
        console.log('[PreloadScene] Audio context created');
      }
    } catch (error) {
      console.warn('[PreloadScene] Failed to create audio context:', error);
    }
  }
}

export default PreloadScene;
